import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Video, Plus, Mail, Copy, Trash2, Users, Clock, Loader2, UserPlus, X, CheckCircle2, Bell, FileText, Sparkles,
} from 'lucide-react';
import ProtocolDialog from './ProtocolDialog';
import EmailPreviewConfirm from '@/components/email/EmailPreviewConfirm';
import { formatDateWithWeekday } from '@/lib/utils';
import { matchesFreeText } from '@/components/search/SearchBuilder';

const fmtDT = (iso) => (iso ? formatDateWithWeekday(iso, { time: true }) : '—');

export default function ConferencesPage() {
  const navigate = useNavigate();
  const [conferences, setConferences] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [protocolConf, setProtocolConf] = useState(null);
  const [invitePreview, setInvitePreview] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/conferences');
      setConferences(r.data.conferences);
      setCanManage(r.data.can_manage);
    } catch {
      toast.error('Konferenzen konnten nicht geladen werden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openInvitePreview = async (conf) => {
    setInvitingId(conf.id);
    try {
      const r = await api.post(`/conferences/${conf.id}/invite/preview`);
      setInvitePreview({ confId: conf.id, ...r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Voransicht fehlgeschlagen');
    } finally { setInvitingId(null); }
  };

  const confirmInvites = async (routing = {}) => {
    if (!invitePreview?.preview_token) return;
    setInviteSending(true);
    try {
      const r = await api.post(`/conferences/${invitePreview.confId}/invite`, {
        preview_token: invitePreview.preview_token,
        ...routing,
      });
      if (r.data.sent > 0) {
        toast.success(`${r.data.sent} Einladung(en) per E-Mail versendet${r.data.push_notified ? `, ${r.data.push_notified} Push` : ''}`);
      } else if (r.data.errors?.length) {
        toast.error(r.data.errors[0].error || 'Versand fehlgeschlagen');
      } else {
        toast.info('Keine Empfänger mit E-Mail-Adresse');
      }
      setInvitePreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Einladungsversand fehlgeschlagen');
    } finally { setInviteSending(false); }
  };

  const removeConference = async (conf) => {
    if (!window.confirm(`Konferenz „${conf.title}" löschen?`)) return;
    try {
      await api.delete(`/conferences/${conf.id}`);
      toast.success('Konferenz gelöscht');
      load();
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(
      () => toast.success('Gast-Link kopiert'),
      () => toast.error('Kopieren fehlgeschlagen'),
    );
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto w-full">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Video className="h-8 w-8 text-brand-red shrink-0" /> Onlinekonferenzen
            </h1>
            <p className="text-slate-500 mt-1">
              Videokonferenzen mit Lehrkräften, Verwaltung und externen Personen — Beitritt für Externe ohne Login.
            </p>
          </div>
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-red hover:bg-brand-red/90 shrink-0" data-testid="conference-create-btn">
                  <Plus className="h-4 w-4 mr-2" /> Neue Konferenz
                </Button>
              </DialogTrigger>
              <ConferenceDialog onCreated={() => { setDialogOpen(false); load(); }} />
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-brand-red" /></div>
        ) : conferences.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
            <Video className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            Noch keine Konferenzen. {canManage ? 'Lege die erste Konferenz an.' : 'Du wurdest noch zu keiner Konferenz eingeladen.'}
          </div>
        ) : (
          <div className="space-y-4">
            {conferences.map((conf) => (
              <div key={conf.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5"
                   data-testid={`conference-card-${conf.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg font-bold text-slate-900 truncate">{conf.title}</h2>
                      {conf.status === 'cancelled' && (
                        <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">abgesagt</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDT(conf.scheduled_at)}</span>
                      <span>{conf.duration_minutes} Min</span>
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {conf.participants.length} Teilnehmer</span>
                      {conf.reminder_sent_at ? (
                        <span className="flex items-center gap-1 text-emerald-600"><Bell className="h-3.5 w-3.5" /> Erinnerung versendet</span>
                      ) : conf.reminder_minutes_before > 0 && conf.status === 'scheduled' ? (
                        <span className="flex items-center gap-1"><Bell className="h-3.5 w-3.5" /> Erinnerung {conf.reminder_minutes_before} Min vorher</span>
                      ) : null}
                    </p>
                    {conf.description && <p className="text-sm text-slate-600 mt-2">{conf.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button onClick={() => navigate(`/conferences/${conf.id}/room`)}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid={`conference-join-${conf.id}`}>
                      <Video className="h-4 w-4 mr-2" /> Beitreten
                    </Button>
                    {canManage && (
                      <>
                        <Button variant="outline" onClick={() => setProtocolConf(conf)}
                                className="border-slate-300" data-testid={`conference-protocol-${conf.id}`}>
                          <FileText className="h-4 w-4 mr-2" />
                          Protokoll
                          {conf.recording_status === 'processing' && <Loader2 className="h-3 w-3 ml-1.5 animate-spin text-brand-red" />}
                          {conf.ai_summary && conf.recording_status === 'done' && <Sparkles className="h-3 w-3 ml-1.5 text-brand-red" />}
                        </Button>
                        <Button variant="outline" onClick={() => openInvitePreview(conf)} disabled={invitingId === conf.id}
                                className="border-brand-red/20 text-brand-red hover:bg-brand-red/5"
                                data-testid={`conference-invite-${conf.id}`}>
                          {invitingId === conf.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                          Einladungen senden
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeConference(conf)}
                                className="text-slate-400 hover:text-red-600" data-testid={`conference-delete-${conf.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {conf.participants.map((p) => (
                    <span key={p.id}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                            p.is_internal
                              ? 'bg-brand-red/5 text-brand-red border-brand-red/20'
                              : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {p.invited_at && <CheckCircle2 className="h-3 w-3 text-emerald-500" title="Eingeladen" />}
                      {p.name || p.email || '—'}
                      {!p.is_internal && <span className="text-[10px] text-slate-400">extern</span>}
                      {canManage && (
                        <button onClick={() => copyLink(p.guest_link)} title="Gast-Link kopieren"
                                className="hover:text-brand-red" data-testid={`copy-link-${p.id}`}>
                          <Copy className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <EmailPreviewConfirm
          open={!!invitePreview}
          onClose={() => setInvitePreview(null)}
          onConfirm={confirmInvites}
          confirming={inviteSending}
          title="Einladung — Voransicht"
          description="Bitte Einladungstext prüfen. Versand erst nach Bestätigung."
          to={invitePreview?.recipient}
          toLabel={invitePreview?.recipient_name}
          recipients={invitePreview?.recipients}
          subject={invitePreview?.subject}
          html={invitePreview?.html}
          sampleNote={invitePreview?.is_sample
            ? `Beispiel für 1 von ${invitePreview.eligible_count} Empfängern.`
            : null}
          confirmLabel={`An ${invitePreview?.eligible_count ?? 0} Empfänger senden`}
          testId="conference-invite-preview"
        />

        {protocolConf && (
          <ProtocolDialog conference={protocolConf} open={!!protocolConf}
                          onClose={() => setProtocolConf(null)} onChanged={load} />
        )}
      </div>
    </Layout>
  );
}

function ConferenceDialog({ onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [duration, setDuration] = useState(60);
  const [reminder, setReminder] = useState(30);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [externals, setExternals] = useState([]);
  const [extEmail, setExtEmail] = useState('');
  const [extName, setExtName] = useState('');
  const [userFilter, setUserFilter] = useState('');

  useEffect(() => {
    api.get('/admin/rbac/users').then(r => setUsers(r.data.filter(u => u.is_active))).catch(() => {});
  }, []);

  const toggleUser = (u) => {
    setSelectedUsers(sel => sel.some(x => x.id === u.id) ? sel.filter(x => x.id !== u.id) : [...sel, u]);
  };

  const addExternal = () => {
    const email = extEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error('Bitte gültige E-Mail-Adresse eingeben');
    if (externals.some(x => x.email === email)) return;
    setExternals([...externals, { email, name: extName.trim() || null }]);
    setExtEmail(''); setExtName('');
  };

  const save = async () => {
    if (!title.trim()) return toast.error('Bitte Titel angeben');
    if (!date) return toast.error('Bitte Datum angeben');
    setSaving(true);
    try {
      await api.post('/conferences', {
        title, description: description || null,
        scheduled_at: `${date}T${time}:00`,
        duration_minutes: duration,
        reminder_minutes_before: reminder,
        participants: [
          ...selectedUsers.map(u => ({ user_id: u.id })),
          ...externals,
        ],
      });
      toast.success('Konferenz angelegt — Einladungen können jetzt versendet werden');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const filteredUsers = users.filter(u =>
    matchesFreeText(u, userFilter));

  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Neue Onlinekonferenz</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-2">
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">Titel *</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. Lehrerkonferenz Q3"
                 className="mt-1" data-testid="conference-title" autoFocus />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Datum *</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1" data-testid="conference-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Uhrzeit</Label>
            <Input type="time" value={time} onChange={e => setTime(e.target.value)} className="mt-1" data-testid="conference-time" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Dauer (Min)</Label>
            <Input type="number" min={15} step={15} value={duration}
                   onChange={e => setDuration(Number(e.target.value) || 60)} className="mt-1" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">Beschreibung (optional)</Label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <Users className="h-3 w-3" /> Interne Teilnehmer (Lehrkräfte, Verwaltung)
          </Label>
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {selectedUsers.map(u => (
                <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-brand-red/5 text-brand-red border border-brand-red/20">
                  {u.full_name || u.username}
                  <button onClick={() => toggleUser(u)}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          <Input value={userFilter} onChange={e => setUserFilter(e.target.value)} placeholder="Benutzer suchen…"
                 className="mt-1.5" data-testid="conference-user-filter" />
          <div className="mt-1 max-h-36 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
            {filteredUsers.slice(0, 30).map(u => (
              <button key={u.id} onClick={() => toggleUser(u)} data-testid={`conference-user-${u.id}`}
                      className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between hover:bg-slate-50 ${
                        selectedUsers.some(x => x.id === u.id) ? 'bg-brand-red/5' : ''}`}>
                <span className="truncate">{u.full_name || u.username} <span className="text-slate-400 text-xs">{u.email}</span></span>
                {selectedUsers.some(x => x.id === u.id) && <CheckCircle2 className="h-4 w-4 text-brand-red shrink-0" />}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <UserPlus className="h-3 w-3" /> Externe Teilnehmer (per E-Mail)
          </Label>
          {externals.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {externals.map((x, i) => (
                <span key={x.email} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-50 text-slate-600 border border-slate-200">
                  {x.name ? `${x.name} (${x.email})` : x.email}
                  <button onClick={() => setExternals(externals.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="grid grid-cols-5 gap-2 mt-1.5">
            <Input value={extEmail} onChange={e => setExtEmail(e.target.value)} placeholder="email@beispiel.de"
                   className="col-span-2" data-testid="conference-ext-email"
                   onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExternal(); } }} />
            <Input value={extName} onChange={e => setExtName(e.target.value)} placeholder="Name (optional)"
                   className="col-span-2" data-testid="conference-ext-name"
                   onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExternal(); } }} />
            <Button variant="outline" onClick={addExternal} data-testid="conference-ext-add">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="w-full bg-brand-red hover:bg-brand-red/90"
                data-testid="conference-save-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Konferenz anlegen
        </Button>
      </div>
    </DialogContent>
  );
}
